import { produce } from 'immer';
import { isNil, findIndex, intersection, keyBy, keys, remove } from 'lodash/fp';
import { isProject } from 'app/routes';
import { SIGN_OUT } from 'app/store/users/types.js';
import { DELETE_UNUSED_DATASETS, DELETE_VIEW } from 'app/store/entities/types.js';
import { extractError } from 'app/lib/error';
import {
    UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED,
    DELETE_PROJECT_SUCCESS_PROJECT_DELETED,
    IMPORT_VIEW_SUCCESS_DATA_SOURCE_IMPORTED,
    UPDATE_PROJECT_OWNER_SUCCESS_PROJECT_OWNER_UPDATED
} from 'app/store/__generated__/events';
import { REHYDRATE } from 'app/store/types.js';
import { ADD_DATASETS_TO_PROJECT } from 'app/store/addViews/types.js';
import { fqnEquals } from 'app/lib/utils';
import {
    LOAD_PROJECTS,
    LOAD_PROJECT,
    SET_CURRENT_ENTITY,
    CREATE_PROJECT,
    CLONE_PROJECT,
    OPEN_CREATE_PROJECT_DIALOG,
    CLOSE_CREATE_PROJECT_DIALOG,
    LOAD_UPSTREAM_DATASOURCES,
    GET_PROJECT_INPUTS,
    SELECT_TABLE,
    UPDATE_PROJECT_ENV_ACTION_TYPE,
    TOGGLE_CREATE_PROJECT_DIALOG
} from './types.js';
import { CreateProjectState } from './constants';

export const INITIAL_STATE = {
    isLoading: false,
    all: null,
    errors: {},
    createProjectState: CreateProjectState.IDLE,
    objectToImportToCreatedProject: null,

    currentEntityIdByProjectId: {},

    cloneProjectError: null,
    cloneProjectProgress: false,
    recentlyOpenedProjectsByUser: {},
    sampleProject: {
        isLoading: false,
        slug: null
    },
    currentProjectUpstreamDataSourceFqnsByViewUid: {},
    projectInputs: {
        isLoading: false,
        byProjectUid: {}
    },
    selectedFqn: null,
    isSettingProjectEnv: false
};

const addRecentProject = (previous, payload, user) => {
    if (!isProject()) {
        return previous;
    }
    const { name, slug, assetId } = payload;
    const projectsByUser = previous[user] || [];
    const updatedByUser = [
        { name, slug, assetId },
        ...projectsByUser.filter((entry) => entry.assetId !== assetId)
    ].slice(0, 10);
    return { ...previous, [user]: updatedByUser };
};

const updateRecentProject = (previous, payload) => {
    const { name, slug, assetId } = payload;
    const updated = {};
    Object.keys(previous).forEach((key) => {
        updated[key] = previous[key].map((entry) =>
            entry.assetId === assetId ? { name, slug, assetId } : entry
        );
    });
    return updated;
};

const deleteFromRecentProjects = (previous, spaceUid) => {
    const updated = {};
    Object.keys(previous).forEach((key) => {
        updated[key] = previous[key].filter(({ assetId }) => assetId !== spaceUid);
    });
    return updated;
};

const syncRecentProjects = (previous, payloads, user) => {
    const projectsByUser = previous[user] || [];
    const projectIdsByUser = projectsByUser.map(({ assetId }) => assetId);
    const readableProjects = payloads.map(({ entityId, name, slug }) => ({
        name,
        slug,
        assetId: entityId
    }));
    const projectsByAssetId = keyBy('assetId', readableProjects);
    const updatedByCurrentUser = intersection(keys(projectsByAssetId), projectIdsByUser).map(
        (assetId) => projectsByAssetId[assetId]
    );
    return { ...previous, [user]: updatedByCurrentUser };
};

export const projectsReducer = produce((draft, action) => {
    switch (action.type) {
        case LOAD_PROJECTS.STARTED:
            draft.isLoading = true;
            return;
        case LOAD_PROJECTS.SUCCEEDED: {
            const readableProjects = action.payload.filter(
                ({ isReadable, slug }) => isReadable && !isNil(slug)
            );
            draft.all = readableProjects;
            draft.recentlyOpenedProjectsByUser = syncRecentProjects(
                draft.recentlyOpenedProjectsByUser,
                readableProjects,
                action.meta?.user
            );
            return;
        }
        case LOAD_PROJECTS.ENDED:
        case LOAD_PROJECT.ENDED:
            draft.isLoading = false;
            return;
        case LOAD_PROJECT.STARTED:
            draft.isLoading = true;
            return;
        case LOAD_PROJECT.SUCCEEDED:
            if (action.meta?.user) {
                draft.recentlyOpenedProjectsByUser = addRecentProject(
                    draft.recentlyOpenedProjectsByUser,
                    action.payload,
                    action.meta.user
                );
            }
            return;
        case UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED: {
            const { assetId: entityId, name, slug } = action.payload;
            const index = findIndex({ entityId }, draft.all);
            if (index > -1) {
                draft.all[index].name = name;
                draft.all[index].slug = slug;
            }
            draft.recentlyOpenedProjectsByUser = updateRecentProject(
                draft.recentlyOpenedProjectsByUser,
                action.payload
            );
            return;
        }
        case UPDATE_PROJECT_OWNER_SUCCESS_PROJECT_OWNER_UPDATED: {
            const { assetId: entityId, spaceRef } = action.payload;
            const index = findIndex({ entityId }, draft.all);

            if (index > -1) {
                draft.all[index] = spaceRef;
            }
            break;
        }

        case SET_CURRENT_ENTITY: {
            const { entityId, projectId } = action.payload;
            draft.currentEntityIdByProjectId[projectId] = entityId;
            return;
        }
        case DELETE_VIEW.SUCCEEDED: {
            const { tableFqn, viewRef } = action.payload;
            const { entityId, spaceUid, viewUid } = viewRef;

            if (draft.currentEntityIdByProjectId[spaceUid] === entityId) {
                delete draft.currentEntityIdByProjectId[spaceUid];
            }
            delete draft.currentProjectUpstreamDataSourceFqnsByViewUid[viewUid];

            const inputs = draft.projectInputs.byProjectUid[spaceUid];
            if (tableFqn && inputs) {
                draft.projectInputs.byProjectUid[spaceUid] = remove(
                    (input) => fqnEquals(input, tableFqn),
                    inputs
                );
            }
            return;
        }
        case DELETE_UNUSED_DATASETS.SUCCEEDED: {
            const { projectUid } = action.meta;
            const { datasets } = action.params;

            for (const entityId of datasets) {
                if (draft.currentEntityIdByProjectId[projectUid] === entityId) {
                    delete draft.currentEntityIdByProjectId[projectUid];
                }
                const [, viewUid] = entityId.split('.');
                delete draft.currentProjectUpstreamDataSourceFqnsByViewUid[viewUid];
            }
            return;
        }
        case DELETE_UNUSED_DATASETS.PROGRESS: {
            const { projectUid } = action.meta;
            const { tableFqn } = action.payload;

            const inputs = draft.projectInputs.byProjectUid[projectUid];
            if (inputs) {
                draft.projectInputs.byProjectUid[projectUid] = remove(
                    (input) => fqnEquals(input, tableFqn),
                    inputs
                );
            }
            return;
        }
        case DELETE_PROJECT_SUCCESS_PROJECT_DELETED: {
            const { assetId } = action.payload;
            delete draft.currentEntityIdByProjectId[assetId];
            draft.recentlyOpenedProjectsByUser = deleteFromRecentProjects(
                draft.recentlyOpenedProjectsByUser,
                assetId
            );
            return;
        }
        case OPEN_CREATE_PROJECT_DIALOG: {
            draft.createProjectState = CreateProjectState.DIALOG_OPEN;
            draft.objectToImportToCreatedProject = action.params.objectToImport;
            draft.isAddingExplorations = action.params.isAddingExplorations;
            break;
        }
        case CLOSE_CREATE_PROJECT_DIALOG: {
            draft.createProjectState = CreateProjectState.IDLE;
            draft.objectToImportToCreatedProject = null;
            draft.isAddingExplorations = null;
            break;
        }
        case TOGGLE_CREATE_PROJECT_DIALOG: {
            draft.createProjectState =
                draft.createProjectState === CreateProjectState.IDLE
                    ? CreateProjectState.DIALOG_OPEN
                    : CreateProjectState.IDLE;
            draft.objectToImportToCreatedProject = null;
            draft.isAddingExplorations = null;
            break;
        }
        case CREATE_PROJECT.STARTED: {
            draft.createProjectState = CreateProjectState.IN_PROGRESS;
            break;
        }
        case CREATE_PROJECT.SUCCEEDED: {
            const { uid, name, slug, envUid } = action.payload;

            draft.currentSpaceId = null;
            draft.createProjectState = CreateProjectState.IDLE;
            draft.objectToImportToCreatedProject = null;
            draft.all.push({
                entityId: uid,
                exists: true,
                isReadable: true,
                isDeletable: true,
                isOwnerModifiable: true,
                uid,
                name,
                slug,
                envUid
            });
            draft.recentlyOpenedProjectsByUser = addRecentProject(
                draft.recentlyOpenedProjectsByUser,
                { assetId: uid, name, slug },
                action.params.user
            );
            break;
        }
        case CREATE_PROJECT.REJECTED:
        case CREATE_PROJECT.FAILED:
            draft.createProjectState = CreateProjectState.IDLE;
            draft.objectToImportToCreatedProject = null;
            break;
        case CLONE_PROJECT.STARTED:
            draft.cloneProjectError = null;
            draft.cloneProjectProgress = true;
            break;
        case CLONE_PROJECT.REJECTED:
        case CLONE_PROJECT.FAILED:
            draft.cloneProjectError = extractError(action.payload);
            draft.cloneProjectProgress = false;
            break;
        case CLONE_PROJECT.SUCCEEDED:
            draft.cloneProjectProgress = false;
            break;
        case REHYDRATE:
            if ('projects' in action.payload) {
                Object.assign(draft, action.payload.projects);
            }
            break;
        case LOAD_UPSTREAM_DATASOURCES.SUCCEEDED:
            draft.currentProjectUpstreamDataSourceFqnsByViewUid = action.payload.tablesByUid;
            break;
        case GET_PROJECT_INPUTS.STARTED:
            draft.projectInputs.isLoading = true;
            break;
        case GET_PROJECT_INPUTS.SUCCEEDED:
            draft.projectInputs.byProjectUid[action.params.spaceUid] = action.payload.inputs;
            break;
        case GET_PROJECT_INPUTS.ENDED:
            draft.projectInputs.isLoading = false;
            break;
        case ADD_DATASETS_TO_PROJECT.SUCCEEDED: {
            const { spaceUid } = action.params;
            delete draft.projectInputs.byProjectUid[spaceUid];
            break;
        }
        case IMPORT_VIEW_SUCCESS_DATA_SOURCE_IMPORTED: {
            const projectUid = action.payload.viewRef.spaceUid;
            delete draft.projectInputs.byProjectUid[projectUid];
            break;
        }
        case SELECT_TABLE:
            draft.selectedFqn = action.payload.fqn;
            break;
        case UPDATE_PROJECT_ENV_ACTION_TYPE.STARTED:
            draft.isSettingProjectEnv = true;
            break;
        case UPDATE_PROJECT_ENV_ACTION_TYPE.SUCCEEDED: {
            const { spaceUid, envUid } = action.params;
            const index = findIndex({ entityId: spaceUid }, draft.all);
            if (index > -1) {
                draft.all[index].envUid = envUid;
            }
            break;
        }
        case UPDATE_PROJECT_ENV_ACTION_TYPE.ENDED:
            draft.isSettingProjectEnv = false;
            break;
        case SIGN_OUT:
            return INITIAL_STATE;
        default:
            break;
    }
}, INITIAL_STATE);
